import type { ComponentProps } from "react";
import type { ClerkProvider, SignIn } from "@clerk/react";

// Supported Clerk appearance keys; no selectors into Clerk's internal DOM.
export const clerkAppearance = {
  options: {
    elevation: "flush",
    // Explicit local visual-review build only; never changes instance identity.
    unsafe_disableDevelopmentModeWarnings: import.meta.env.VITE_AUTH_VISUAL_REVIEW === "1" &&
      import.meta.env.VITE_CLERK_PUBLISHABLE_KEY?.startsWith("pk_test_"),
  },
  variables: {
    fontFamily: "var(--font-ui)",
    fontSize: "15px",
    fontWeight: { normal: 400, medium: 500, semibold: 600, bold: 700 },
    borderRadius: "var(--r-md)",
    colorBackground: "var(--ground)",
    colorForeground: "var(--ink)",
    colorMutedForeground: "var(--ink-2)",
    colorPrimary: "var(--accent)",
    colorPrimaryForeground: "var(--accent-ink)",
    colorInput: "var(--surface-2)",
    colorInputForeground: "var(--ink)",
    colorBorder: "var(--control-border)",
    colorRing: "var(--ring)",
    colorDanger: "var(--neg)",
    colorSuccess: "var(--pos)",
    colorWarning: "var(--warn)",
  },
  elements: {
    rootBox: { width: "100%" },
    cardBox: { width: "100%", boxShadow: "none", borderRadius: 0 },
    card: { background: "transparent", padding: 0, gap: "24px", boxShadow: "none" },
    main: { gap: "24px" },
    header: { textAlign: "left", gap: "8px", alignItems: "flex-start" },
    headerTitle: "auth-heading",
    headerSubtitle: "auth-instructions",
    identityPreview: { justifyContent: "flex-start", gap: "8px", width: "100%" },
    identityPreviewText: { color: "var(--ink)", fontSize: "15px", fontWeight: "var(--fw-medium)" },
    identityPreviewEditButton: "auth-icon-action",
    formFieldLabel: { color: "var(--ink-2)", fontWeight: "var(--fw-medium)" },
    formFieldInput: "auth-input",
    formField: "auth-field",
    formFieldErrorText: { color: "var(--neg)", lineHeight: "1.5", marginTop: "8px" },
    formFieldHintText: { color: "var(--ink-2)", lineHeight: "1.5" },
    form: { gap: "24px" },
    formButtonPrimary: "auth-primary",
    formFieldInputShowPasswordButton: "auth-icon-action",
    alternativeMethodsBlockButton: "auth-method",
    otpCodeField: { alignItems: "stretch", width: "100%" },
    otpCodeFieldInputs: { gap: "8px", justifyContent: "flex-start" },
    otpCodeFieldInput: "auth-code-input",
    otpCodeFieldErrorText: { width: "100%", color: "var(--neg)", fontSize: "15px", lineHeight: "1.5", textAlign: "left" },
    // Match Notice's contextual surface, border, icon and typography in Clerk's
    // own alert. Clerk retains its role, announcement, content and state.
    alert: { background: "var(--surface-2)", border: "1px solid var(--hair)", borderRadius: "var(--r-md)", padding: "12px", gap: "12px", boxShadow: "none" },
    alertText: { color: "var(--ink)", fontFamily: "var(--font-ui)", fontSize: "var(--fs-ui)", lineHeight: "1.5" },
    alertIcon: { width: "18px", height: "18px" },
    alertIcon__danger: { color: "var(--neg)" },
    alertIcon__warning: { color: "var(--warn)" },
    alertIcon__info: { color: "var(--info)" },
    formResendCodeLink: "auth-text-action",
    alternativeMethodsBlockButtonText: { fontSize: "15px", fontWeight: "var(--fw-medium)", whiteSpace: "normal", overflow: "visible", textOverflow: "clip", textAlign: "left", lineHeight: "1.5" },
    footerActionLink: "auth-text-action",
    backLink: "auth-text-action",
    formFieldAction: "auth-text-action",
    footer: { background: "transparent" },
    footerAction: "auth-account-action",
    footerActionText: { color: "var(--ink-2)" },
  },
} satisfies NonNullable<ComponentProps<typeof SignIn>["appearance"]>;

// Copy belongs to Clerk's actual step. Do not claim delivery or successful
// verification: challenges, errors and verification remain Clerk-owned.
export const clerkLocalization = {
  signIn: {
    start: { title: "Sign in", subtitle: "Welcome back. Enter your email to continue.", actionText: "New to SignalBill?", actionLink: "Create an account" },
    password: { title: "Enter your password", subtitle: "Use the password for this account." },
    alternativeMethods: { title: "Another way to sign in", subtitle: "Choose a sign-in method for your account." },
    emailCode: { title: "Check your email", subtitle: "Enter the six-digit sign-in code for", formTitle: "Enter your six-digit code" },
    emailCodeMfa: { title: "Check your email", subtitle: "Enter the six-digit verification code for", formTitle: "Enter your six-digit code" },
  },
  signUp: { start: { title: "Create your SignalBill account", subtitle: "", actionText: "Already have an account?", actionLink: "Sign in" } },
} satisfies NonNullable<ComponentProps<typeof ClerkProvider>["localization"]>;
